import { Box, ToggleButton, ToggleButtonGroup, Typography, Skeleton } from '@mui/material'
import { keyframes } from '@mui/material/styles'
import { ResponsiveLine, Serie } from '@nivo/line'
import { FC, useMemo, useState } from 'react'
import { useTranslate } from 'react-admin'
import { xxllncColor } from '../layout/theme/color'

type Period = '24h' | '7d' | '30d'

interface LogsChartProps {
  data: Array<{ created: string; is_success: boolean }> | undefined
  isLoading: boolean
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`

const R = '16px'

const COLORS = {
  total: xxllncColor.dark100,
  totalFill: xxllncColor.opDark10,
  errors: xxllncColor.red100,
  errorsFill: xxllncColor.opRed10,
}

const periodBuckets = (period: Period): { format: (d: Date) => string; floor: (d: Date) => Date; step: number; cutoff: number } => {
  switch (period) {
    case '24h':
      return {
        format: (d) => `${String(d.getHours()).padStart(2, '0')}:00`,
        floor: (d) => { const r = new Date(d); r.setMinutes(0, 0, 0); return r },
        step: 3600000,
        cutoff: 24 * 3600000,
      }
    case '7d':
      return {
        format: (d) => d.toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric' }),
        floor: (d) => { const r = new Date(d); r.setHours(0, 0, 0, 0); return r },
        step: 86400000,
        cutoff: 7 * 86400000,
      }
    case '30d':
      return {
        format: (d) => d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' }),
        floor: (d) => { const r = new Date(d); r.setHours(0, 0, 0, 0); return r },
        step: 86400000,
        cutoff: 30 * 86400000,
      }
  }
}

const buildSeries = (
  logs: Array<{ created: string; is_success: boolean }>,
  period: Period
): { series: Serie[]; totalCount: number; errorCount: number } => {
  const { format, floor, step, cutoff } = periodBuckets(period)
  const now = new Date()
  const start = floor(new Date(now.getTime() - cutoff))

  const buckets = new Map<string, { total: number; errors: number; date: Date }>()
  for (let t = start.getTime(); t <= now.getTime(); t += step) {
    const d = new Date(t)
    const key = format(d)
    if (!buckets.has(key)) {
      buckets.set(key, { total: 0, errors: 0, date: d })
    }
  }

  let totalCount = 0
  let errorCount = 0
  for (const log of logs) {
    const d = new Date(log.created)
    if (d.getTime() < start.getTime()) continue
    const key = format(floor(d))
    const bucket = buckets.get(key)
    if (bucket) {
      bucket.total++
      totalCount++
      if (!log.is_success) {
        bucket.errors++
        errorCount++
      }
    }
  }

  const entries = Array.from(buckets.values())

  const series: Serie[] = [
    {
      id: 'total',
      data: entries.map((e) => ({ x: format(e.date), y: e.total })),
    },
    {
      id: 'errors',
      data: entries.map((e) => ({ x: format(e.date), y: e.errors })),
    },
  ]

  return { series, totalCount, errorCount }
}

export const LogsChart: FC<LogsChartProps> = ({ data, isLoading }) => {
  const translate = useTranslate()
  const [period, setPeriod] = useState<Period>('7d')

  const { series, totalCount, errorCount } = useMemo(
    () => buildSeries(data ?? [], period),
    [data, period]
  )

  if (isLoading) {
    return (
      <Box sx={{ background: '#fff', borderRadius: R, border: '1px solid rgba(0,0,0,0.06)', p: 3, mb: 2 }}>
        <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '8px' }} />
      </Box>
    )
  }

  return (
    <Box sx={{
      background: '#fff', borderRadius: R, border: '1px solid rgba(0,0,0,0.06)',
      p: 3, mb: 2, animation: `${fadeIn} 0.4s ease-out both`,
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS.total }} />
            <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>
              {translate('logsChart.total')}
            </Typography>
            <Typography sx={{
              fontSize: 18, fontWeight: 700, color: COLORS.total,
              fontVariantNumeric: 'tabular-nums', ml: 0.5,
            }}>
              {totalCount}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS.errors }} />
            <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>
              {translate('logsChart.errors')}
            </Typography>
            <Typography sx={{
              fontSize: 18, fontWeight: 700, color: COLORS.errors,
              fontVariantNumeric: 'tabular-nums', ml: 0.5,
            }}>
              {errorCount}
            </Typography>
          </Box>
        </Box>

        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(_, v: Period | null) => { if (v) setPeriod(v) }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              'textTransform': 'none',
              'fontWeight': 600,
              'fontSize': 11,
              'px': 1.5,
              'py': 0.25,
              'border': '1px solid rgba(0,0,0,0.08)',
              'color': 'rgba(0,0,0,0.4)',
              '&.Mui-selected': {
                'bgcolor': xxllncColor.opDark10,
                'color': xxllncColor.dark100,
                'borderColor': xxllncColor.opDark20,
                '&:hover': { bgcolor: xxllncColor.opDark20 },
              },
            },
          }}
        >
          <ToggleButton value="24h">{translate('logsChart.period24h')}</ToggleButton>
          <ToggleButton value="7d">{translate('logsChart.period7d')}</ToggleButton>
          <ToggleButton value="30d">{translate('logsChart.period30d')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ height: 200 }}>
        <ResponsiveLine
          data={series}
          margin={{ top: 8, right: 12, bottom: 28, left: 36 }}
          xScale={{ type: 'point' }}
          yScale={{ type: 'linear', min: 0, stacked: false }}
          curve="monotoneX"
          enableArea={true}
          areaOpacity={0.06}
          colors={[COLORS.total, COLORS.errors]}
          lineWidth={2}
          enablePoints={false}
          enableGridX={false}
          gridYValues={4}
          axisBottom={{
            tickSize: 0,
            tickPadding: 10,
            tickRotation: period === '30d' ? -45 : 0,
            tickValues: period === '24h'
              ? series[0]?.data.filter((_, i) => i % 3 === 0).map((d) => d.x as string)
              : period === '30d'
                ? series[0]?.data.filter((_, i) => i % 5 === 0).map((d) => d.x as string)
                : undefined,
          }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 8,
            tickValues: 4,
          }}
          theme={{
            axis: {
              ticks: {
                text: { fill: 'rgba(0,0,0,0.3)', fontSize: 10, fontWeight: 500 },
              },
            },
            grid: {
              line: { stroke: 'rgba(0,0,0,0.04)', strokeWidth: 1 },
            },
            crosshair: {
              line: { stroke: xxllncColor.opDark40, strokeWidth: 1, strokeDasharray: '4 4' },
            },
          }}
          useMesh={true}
          tooltip={({ point }) => (
            <Box sx={{
              background: '#1a1a1a', color: '#fff', borderRadius: '8px',
              px: 1.5, py: 0.75, fontSize: 12, fontWeight: 500,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}>
              <span style={{ color: point.serieId === 'total' ? COLORS.total : COLORS.errors, fontWeight: 700 }}>
                {point.data.yFormatted}
              </span>
              {' '}
              {point.serieId === 'total' ? translate('logsChart.total').toLowerCase() : translate('logsChart.errors').toLowerCase()}
              <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: 8 }}>{point.data.xFormatted}</span>
            </Box>
          )}
        />
      </Box>
    </Box>
  )
}
